/**
 * Registration Page (DC-892)
 *
 * Registration page for new organizations.
 * Creates organization and admin user account.
 */

import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { PasswordStrengthIndicator, isPasswordValid } from '../../components/auth/PasswordStrengthIndicator';
import '../../styles/auth.css';

export function Register() {
  const _navigate = useNavigate();
  const { register, isLoading } = useAuth();

  const [formData, setFormData] = useState({
    organizationName: '',
    fullName: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.organizationName.trim()) {
      setError('Please enter your organization name');
      return;
    }

    if (!formData.fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!isPasswordValid(formData.password)) {
      setError('Password does not meet the requirements');
      return;
    }

    const result = await register(formData);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">DATA COMPASS</div>
            </div>

            <div className="auth-success" style={{ marginBottom: '1.5rem' }}>
              <span>✓</span>
              Registration successful!
            </div>

            <h1 className="auth-title">Check your email</h1>

            <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
              We've sent a verification link to <strong>{formData.email}</strong>.
              Please check your inbox and click the link to verify your account.
            </p>

            <Link to="/login" className="auth-button" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-text">DATA COMPASS</div>
          </div>

          <h1 className="auth-title">Create your account</h1>
          <p className="auth-subtitle">Start your data quality journey</p>

          {/* Error message */}
          {error && (
            <div className="auth-error">
              <span className="auth-error-icon">!</span>
              {error}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Organization Name */}
            <div className="auth-field">
              <label htmlFor="organizationName" className="auth-label">
                Organization Name
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="organizationName"
                  name="organizationName"
                  type="text"
                  className="auth-input"
                  placeholder="Acme Pharma Inc."
                  value={formData.organizationName}
                  onChange={handleChange}
                  autoComplete="organization"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Full Name */}
            <div className="auth-field">
              <label htmlFor="fullName" className="auth-label">
                Your Full Name
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  className="auth-input"
                  placeholder="Jane Smith"
                  value={formData.fullName}
                  onChange={handleChange}
                  autoComplete="name"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Email */}
            <div className="auth-field">
              <label htmlFor="email" className="auth-label">
                Work Email
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="auth-input"
                  placeholder="jane@acmepharma.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password */}
            <div className="auth-field">
              <label htmlFor="password" className="auth-label">
                Password
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              <PasswordStrengthIndicator password={formData.password} />
            </div>

            {/* Submit button */}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="auth-button-spinner" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="auth-links">
            <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
